import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import useSEO from "../lib/useSEO";
import { Loader2 } from "lucide-react";

const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
        <path d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18Z" fill="#4285F4"/>
        <path d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2.04a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17Z" fill="#34A853"/>
        <path d="M4.5 10.48a4.8 4.8 0 0 1 0-3.04V5.37H1.83a8 8 0 0 0 0 7.18l2.67-2.07Z" fill="#FBBC05"/>
        <path d="M8.98 4.72c1.16 0 2.23.4 3.06 1.2L14.32 3.6A8 8 0 0 0 1.83 5.37L4.5 7.44a4.77 4.77 0 0 1 4.48-2.72Z" fill="#EA4335"/>
    </svg>
);

export default function AttorneyAuthPage() {
    const [mode, setMode] = useState("login");
    const [form, setForm] = useState({ email: "", password: "", name: "", firm_name: "" });
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const { login, register } = useAuth();
    const navigate = useNavigate();
    const [search] = useSearchParams();

    useSEO({
        title: "Attorney Sign In / Register",
        description:
            "Sign in to your LocalLawFinder attorney dashboard or register your practice to claim Basic, Pro, or Elite placement in the counties you serve.",
        path: "/attorney/auth",
    });

    useEffect(() => {
        if (search.get("google_error")) setErr("Google sign-in didn't complete. Try again.");
    }, [search]);

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setLoading(true);
        try {
            // Pick up the referral code from URL or sessionStorage (set by /join?ref=)
            const refFromUrl = (search.get("ref") || "").trim().toUpperCase();
            let ref = refFromUrl;
            if (!ref) {
                try { ref = (sessionStorage.getItem("ll_ref") || "").toUpperCase(); } catch { /* ignore */ }
            }
            const user = mode === "login"
                ? await login(form.email, form.password)
                : await register({ ...form, ref: ref || undefined });
            // Once consumed, clear so a later registration on the same tab doesn't double-attribute.
            if (mode === "register" && ref) { try { sessionStorage.removeItem("ll_ref"); } catch { /* ignore */ } }
            if (user.role === "admin") navigate("/admin/dashboard");
            else if (user.role === "affiliate") navigate("/affiliate/dashboard");
            else navigate("/attorney/dashboard");
        } catch (e2) {
            setErr(e2?.response?.data?.detail || "Something went wrong");
        } finally { setLoading(false); }
    };

    const startGoogle = () => {
        const redirect = `${window.location.origin}/auth/callback`;
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
    };

    return (
        <section className="max-w-md mx-auto px-4 sm:px-6 py-16">
            <div className="ll-label mb-3">Attorney Side</div>
            <h1 className="font-serif font-bold text-3xl sm:text-4xl tracking-tight mb-2" data-testid="attorney-auth-title">
                {mode === "login" ? "Sign in to your practice" : "Register your practice"}
            </h1>
            <p className="text-sm text-muted-foreground mb-8">
                Buy visibility county by county. Pay only for the markets you serve.
            </p>

            <div className="ll-card">
                {/* Google Sign-In */}
                <button
                    type="button"
                    onClick={startGoogle}
                    className="w-full inline-flex items-center justify-center gap-3 rounded-full border border-border bg-card px-5 py-3 font-semibold hover:bg-accent transition-colors mb-5"
                    data-testid="google-signin-btn">
                    <GoogleIcon />
                    Continue with Google
                </button>
                <div className="relative my-5">
                    <div className="ll-divider" />
                    <span className="absolute inset-0 flex justify-center -translate-y-1/2 pointer-events-none">
                        <span className="bg-card px-3 text-[11px] uppercase tracking-widest text-muted-foreground">or with email</span>
                    </span>
                </div>

                <div className="flex bg-accent rounded-full p-1 mb-6 text-sm">
                    <button onClick={() => setMode("login")}
                            className={`flex-1 py-2 rounded-full font-medium transition ${mode === "login" ? "bg-card shadow-sm" : ""}`}
                            data-testid="auth-tab-login">Sign in</button>
                    <button onClick={() => setMode("register")}
                            className={`flex-1 py-2 rounded-full font-medium transition ${mode === "register" ? "bg-card shadow-sm" : ""}`}
                            data-testid="auth-tab-register">Register</button>
                </div>

                <form onSubmit={submit} className="space-y-4">
                    {mode === "register" && (
                        <>
                            <div>
                                <label className="ll-label block mb-1.5">Full name</label>
                                <input className="ll-input" required value={form.name}
                                       onChange={e => setForm({ ...form, name: e.target.value })}
                                       data-testid="register-name-input" />
                            </div>
                            <div>
                                <label className="ll-label block mb-1.5">Firm name (optional)</label>
                                <input className="ll-input" value={form.firm_name}
                                       onChange={e => setForm({ ...form, firm_name: e.target.value })}
                                       data-testid="register-firm-input" />
                            </div>
                        </>
                    )}
                    <div>
                        <label className="ll-label block mb-1.5">Email</label>
                        <input type="email" className="ll-input" required value={form.email}
                               onChange={e => setForm({ ...form, email: e.target.value })}
                               data-testid="auth-email-input" />
                    </div>
                    <div>
                        <label className="ll-label block mb-1.5">Password</label>
                        <input type="password" className="ll-input" required minLength={6} value={form.password}
                               onChange={e => setForm({ ...form, password: e.target.value })}
                               data-testid="auth-password-input" />
                    </div>
                    {err && <div className="text-sm text-destructive" data-testid="auth-error">{err}</div>}
                    <button type="submit" disabled={loading}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-3 font-semibold hover:bg-primary/90 disabled:opacity-60"
                            data-testid="auth-submit-btn">
                        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                        {mode === "login" ? "Sign in" : "Create account"}
                    </button>
                </form>
            </div>
        </section>
    );
}
